import { Currency, VerifyPayeeCheckResponseCode } from '@seapoint/server/enums';
import { useEffect, useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { PayrollView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { payrollStorageHelpers } from '../../utils/payroll-storage';
import { PayrollSummaryCard } from './payroll-summary-card';
import { PayrollSummaryTable } from './payroll-summary-table/payroll-summary-table';

type PayrollApprovalWrapperProps = {
  payroll?: PayrollView | null;
  currency?: Currency;
  isVopRequired: boolean;
  isLoading: boolean;
  onContinue: () => Promise<void>;
  onEditPayrollRecord: (payrollRecordId: string) => void;
};

export const PayrollApprovalWrapper = ({
  payroll,
  currency,
  isVopRequired,
  isLoading,
  onContinue,
  onEditPayrollRecord,
}: PayrollApprovalWrapperProps) => {
  const [acknowledgedRecordIds, setAcknowledgedRecordIds] = useState<Set<string>>(() => {
    if (!payroll?.id) {
      return new Set();
    }
    return payrollStorageHelpers.getAcknowledgedRecords(payroll.id);
  });

  useEffect(() => {
    if (!payroll?.id) {
      return;
    }
    payrollStorageHelpers.setAcknowledgedRecords(payroll.id, acknowledgedRecordIds);
  }, [acknowledgedRecordIds, payroll?.id]);

  const canApprovePayroll =
    payroll &&
    payroll.records.every((r) => {
      const isPayeeCheckMatched = r.employee.latestPayeeCheck?.code === VerifyPayeeCheckResponseCode.MATCHED;
      const hasAcknowledgedRecordId = acknowledgedRecordIds.has(r.id);
      const isEligibleForEdit = payrollStorageHelpers.isRecordEligibleForEdit(payroll.id, r.id);

      return isPayeeCheckMatched || hasAcknowledgedRecordId || !isEligibleForEdit || !isVopRequired;
    });

  const handleEditPayrollRecord = (payrollRecordId: string) => {
    setAcknowledgedRecordIds((prev) => new Set(prev).add(payrollRecordId));

    onEditPayrollRecord(payrollRecordId);
  };

  if (!currency || !payroll) {
    return null;
  }
  const totalNetPay = payroll.records.reduce((acc, record) => acc + record.netPay, 0);

  return (
    <Stack space={4} className="mt-10 w-full" align="end">
      <Inline className="w-full">
        <div className="flex-1">
          <PayrollSummaryCard
            payrollName={payroll.name}
            country={payroll.country}
            period={payroll.period}
            company={payroll.company.legalEntity.name}
            status={payroll.status}
          />
        </div>
        <Stack className="rounded-lg bg-white px-5 py-4" align="start" space={2}>
          <p className="text-sm text-muted-foreground">Employees</p>
          <p className="text-lg font-semibold">{payroll.records.length}</p>
        </Stack>
        <Stack className="shrink-0 rounded-lg bg-white px-5 py-4" align="end" space={2}>
          <p className="text-sm text-muted-foreground">Total net pay</p>
          <p className="text-lg font-semibold">
            {getAmountWithCurrency({
              amount: totalNetPay,
              currency: payroll.currency,
              isAccountBalance: true,
            })}
          </p>
        </Stack>
      </Inline>
      <Inline space={2} className="mt-4 w-full" wrap="nowrap">
        <h2 className="text-xl font-semibold">Payroll</h2>
      </Inline>
      <PayrollSummaryTable records={payroll.records} onEditPayrollRecord={handleEditPayrollRecord} />

      <Button
        onClick={async () => {
          await onContinue();
          payrollStorageHelpers.removeAcknowledgedRecords(payroll.id);
        }}
        isLoading={isLoading}
        disabled={isLoading || !canApprovePayroll}
      >
        Approve
      </Button>
    </Stack>
  );
};
